import { lazy, Suspense, useState } from "react";
import { Link } from "react-router-dom";
import BlogSampleOfferSlot from "@/components/feature/BlogSampleOfferSlot";

const DiscountPopup = lazy(() => import("./DiscountPopup"));

const footerCols = [
  { title: "Letters & Evaluations", links: [
    {label:"ESA Letter Online",href:"/esa-letter-online"},
    {label:"PSD Evaluation",href:"/psychiatric-service-dog-evaluation"},
    {label:"ESA Letter Pricing",href:"/esa-letter-cost"},
    {label:"PSD Letter Pricing",href:"/psd-letter-cost"},
    {label:"Renew Your ESA Letter",href:"/renew-esa-letter"},
  ]},
  { title: "Guides & Resources", links: [
    {label:"How to Get an ESA Letter",href:"/how-to-get-esa-letter"},
    {label:"How to Get a PSD Letter",href:"/how-to-get-psd-letter"},
    {label:"ESA Letter for Landlords",href:"/esa-letter-for-landlord"},
    {label:"Resource Center",href:"/resource-center"},
    {label:"Blog",href:"/blog"},
  ]},
  { title: "Explore", links: [
    {label:"ESA Letters by State",href:"/explore-esa-letters-all-states"},
    {label:"PSD Letters by State",href:"/explore-psd-letters-all-states"},
    {label:"Service Animals vs. ESAs",href:"/service-animal-vs-esa"},
    {label:"Housing Rights",href:"/housing-rights-esa"},
    {label:"Sitemap",href:"/sitemap"},
  ]},
  { title: "Company", links: [
    {label:"About Us",href:"/about-us"},
    {label:"Our Providers",href:"/our-providers"},
    {label:"For Therapists",href:"/join-our-network"},
    {label:"FAQs",href:"/faqs"},
    {label:"Contact Us",href:"/contact-us"},
    {label:"Refund Policy",href:"/refund-policy"},
  ]},
];

const socialLinks = [
  { icon: "ri-facebook-fill", href: "https://www.facebook.com/PawTenant/", label: "Facebook" },
  { icon: "ri-instagram-line", href: "https://www.instagram.com/pawtenant/", label: "Instagram" },
];

export default function SharedFooter() {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (title: string) => {
    setOpenSection((prev) => (prev === title ? null : title));
  };

  return (
    <>
    {/* BLOG-LEAD-MAGNET-SAMPLES-001: free 15-page sample form — renders only on classified blog articles. */}
    <BlogSampleOfferSlot />
    <footer className="bg-[#f0ece4]">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-5 pt-12 pb-8 lg:pt-16 lg:pb-10">
        {/* Brand — always full width on mobile, then in grid on desktop */}
        <div className="mb-8 lg:hidden">
          {/* Phase 7 PageSpeed (2026-05-26): WebP variants replace 115 KB PNG. */}
          <Link to="/" className="inline-block mb-4 cursor-pointer">
            <picture>
              <source
                type="image/webp"
                srcSet="/assets/brand/pawtenant-logo-black-320.webp 1x, /assets/brand/pawtenant-logo-black-640.webp 2x"
              />
              <img
                src="/assets/brand/pawtenant-logo-black-02.png"
                alt="PawTenant Logo"
                width={400}
                height={160}
                loading="lazy"
                decoding="async"
                className="h-12 w-auto object-contain object-left"
              />
            </picture>
          </Link>
          <p className="text-gray-600 text-sm leading-relaxed max-w-xs">
            Your trusted partner in fast, legitimate ESA Letter consultations — empowering lives through emotional support animals.
          </p>
        </div>

        {/* Desktop Grid */}
        <div className="hidden lg:grid grid-cols-5 gap-8">
          {/* Brand Col */}
          <div>
            {/* Phase 7 PageSpeed (2026-05-26): WebP variants replace 115 KB PNG. */}
            <Link to="/" className="inline-block mb-4 cursor-pointer">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/assets/brand/pawtenant-logo-black-320.webp 1x, /assets/brand/pawtenant-logo-black-640.webp 2x"
                />
                <img
                  src="/assets/brand/pawtenant-logo-black-02.png"
                  alt="PawTenant Logo"
                  width={400}
                  height={160}
                  loading="lazy"
                  decoding="async"
                  className="h-14 w-auto object-contain object-left"
                />
              </picture>
            </Link>
            <p className="text-gray-600 text-sm leading-relaxed max-w-[220px]">
              Your trusted partner in providing fast, legitimate Emotional Support Animal Letter consultations, empowering lives through the transformative power of emotional support animals.
            </p>
          </div>
          {footerCols.map((col) => (
            <div key={col.title}>
              <h4 className="text-gray-800 font-bold text-sm mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-sm text-gray-600 hover:text-orange-500 transition-colors cursor-pointer"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Mobile Accordion */}
        <div className="lg:hidden divide-y divide-gray-300/40">
          {footerCols.map((col) => (
            <div key={col.title}>
              <button
                className="w-full flex items-center justify-between py-3.5 text-sm font-bold text-gray-800 cursor-pointer"
                onClick={() => toggleSection(col.title)}
              >
                {col.title}
                <i
                  className={`ri-arrow-down-s-line text-gray-500 transition-transform ${
                    openSection === col.title ? "rotate-180" : ""
                  }`}
                ></i>
              </button>
              {openSection === col.title && (
                <ul className="pb-4 space-y-2.5 pl-1">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.href}
                        className="text-sm text-gray-600 hover:text-orange-500 transition-colors cursor-pointer"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Social + Contact Bar */}
      <div className="border-t border-gray-300/60">
        <div className="max-w-7xl mx-auto px-5 py-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* Social Icons */}
          <div className="flex items-center gap-2.5">
            {socialLinks.map((s) => (
              <a
                key={s.label}
                href={s.href}
                rel="nofollow"
                aria-label={s.label}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 hover:text-orange-500 hover:border-orange-300 transition-colors cursor-pointer"
              >
                <i className={`${s.icon} text-base`}></i>
              </a>
            ))}
          </div>

          {/* Contact + Badge */}
          <div className="flex flex-wrap items-center gap-4">
            <a
              href="tel:+14099655885"
              className="text-sm text-gray-700 hover:text-orange-500 transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-phone-line text-orange-500 text-sm"></i>
              (409) 965-5885
            </a>
            <div className="w-px h-4 bg-gray-300 hidden sm:block"></div>
            <a
              href="mailto:hello@pawtenant.com"
              className="text-sm text-gray-700 hover:text-orange-500 transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-mail-line text-orange-500 text-sm"></i>
              hello@pawtenant.com
            </a>
            <div className="w-px h-4 bg-gray-300 hidden sm:block"></div>
            <div className="flex items-center gap-1.5 text-sm text-gray-600">
              <i className="ri-time-line text-orange-500 text-sm"></i>
              <span>Mon–Fri 7am–6pm CT &middot; Sat 9am–4pm CT</span>
            </div>
            <div className="w-px h-4 bg-gray-300 hidden sm:block"></div>
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-3 py-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shield-check-fill text-orange-500 text-sm"></i>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-700 leading-none">HIPAA</p>
                <p className="text-xs text-gray-400 leading-none">COMPLIANT</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="border-t border-gray-300/60">
        <div className="max-w-7xl mx-auto px-5 py-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          <span className="text-xs text-gray-500">
            &copy; {new Date().getFullYear()} PawTenant. All rights reserved.
          </span>
          <span className="text-gray-300 hidden sm:inline">|</span>
          <Link to="/privacy-policy" className="text-xs text-gray-500 hover:text-orange-500 transition-colors cursor-pointer">
            Privacy Policy
          </Link>
          <span className="text-gray-300 hidden sm:inline">|</span>
          <Link to="/terms-of-use" className="text-xs text-gray-500 hover:text-orange-500 transition-colors cursor-pointer">
            Terms &amp; Conditions
          </Link>
          <span className="text-gray-300 hidden sm:inline">|</span>
          <Link to="/sitemap" className="text-xs text-gray-500 hover:text-orange-500 transition-colors cursor-pointer">
            Sitemap
          </Link>
          <span className="text-gray-300 hidden sm:inline">|</span>
          {/* US-only permanent notice */}
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
            <span role="img" aria-label="US flag">🇺🇸</span>
            <span>Serving US residents only &mdash; All 50 states</span>
          </span>
        </div>
      </div>
    </footer>
    <Suspense fallback={null}><DiscountPopup /></Suspense>
    </>
  );
}
